import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Package, Bot, Globe, Phone, Info, Loader2, Mail } from 'lucide-react';
import { pricingData, type ServiceId, type BusinessModel, type ServiceFeatureId, type SelectedFeatures, calculateTotalCost, calculateDiscount } from '../../data/pricing';
import { submitForm } from '../../utils/forms';

interface ServiceSelectionProps {
  onComplete: (selected: SelectedFeatures[]) => void;
}

export function ServiceSelection({ onComplete }: ServiceSelectionProps) {
  const navigate = useNavigate();
  const [selectedServices, setSelectedServices] = useState<Map<ServiceId, SelectedFeatures>>(new Map());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  const handleModelSelect = (serviceId: ServiceId, model: BusinessModel) => {
    setSelectedServices(prev => {
      const next = new Map(prev);
      const current = next.get(serviceId);
      
      if (current?.model === model) {
        next.delete(serviceId);
      } else {
        next.set(serviceId, {
          serviceId,
          model,
          features: new Set()
        });
      }
      
      return next;
    });
  };

  const toggleFeature = (serviceId: ServiceId, featureId: ServiceFeatureId) => {
    setSelectedServices(prev => {
      const next = new Map(prev);
      const current = next.get(serviceId);
      
      if (!current) return prev;
      
      const features = new Set(current.features);
      if (features.has(featureId)) {
        features.delete(featureId);
      } else {
        features.add(featureId);
      }
      
      next.set(serviceId, { ...current, features });
      return next;
    });
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (!email) {
        setEmailError('Email is required');
        return;
      }

      if (!validateEmail(email)) {
        setEmailError('Please enter a valid email address');
        return;
      }

      setIsSubmitting(true);
      setEmailError('');

      const validSelections = Array.from(selectedServices.values()).filter(
        service => service.features.size > 0
      );

      if (validSelections.length === 0) {
        throw new Error('Please select at least one service and feature');
      }

      const servicesData = validSelections.map(service => ({
        name: pricingData[service.serviceId].name,
        model: service.model,
        features: Array.from(service.features).map(featureId => 
          pricingData[service.serviceId].features[featureId].name
        )
      }));

      const summary = `
Services Selected:
${servicesData.map(service => `
- ${service.name}
  Model: ${service.model}
  Features: ${service.features.join(', ')}
`).join('\n')}

Pricing:
- Setup Cost: $${totalSetupCost}
${discountPercentage > 0 ? `- Bundle Discount (${discountPercentage}%): -$${discountAmount.toFixed(2)}` : ''}
- Final Setup Cost: $${finalCost.toFixed(2)}
${totalMonthly > 0 ? `- Monthly Cost: $${totalMonthly}/month` : ''}
`;

      await submitForm({
        formType: 'Quote',
        email: email,
        message: summary.trim(),
        services: servicesData
      });

      try {
        await fetch('/api/facebook-event', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            eventName: 'InitiateCheckout',
            eventData: {
              customData: {
                content_type: 'product_group',
                contents: validSelections.map(service => ({
                  id: service.serviceId,
                  quantity: 1
                })),
                value: totalSetupCost,
                currency: 'USD'
              }
            }
          })
        });
      } catch (fbError) {
        console.warn('Facebook event tracking failed:', fbError);
      }

      // Navigate to thank you page
      navigate('/booking/thank-you');

    } catch (error) {
      console.error('Form submission error:', error);
      alert('There was an error submitting your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate totals
  const totalSetupCost = Array.from(selectedServices.values()).reduce((total, selected) => {
    const costs = calculateTotalCost(selected);
    return total + costs.setup;
  }, 0);

  const totalMonthly = Array.from(selectedServices.values()).reduce((total, selected) => {
    const costs = calculateTotalCost(selected);
    return total + costs.monthly;
  }, 0);

  const { discountPercentage, discountAmount, finalCost } = calculateDiscount(
    totalSetupCost,
    selectedServices.size
  );

  const hasValidSelections = Array.from(selectedServices.values()).some(s => s.features.size > 0);

  return (
    <div className="space-y-12">
      <div className="text-center">
        <h2 className="text-4xl font-bold text-white mb-4">Select Your Services</h2>
        <p className="text-xl text-gray-400 max-w-2xl mx-auto">
          Choose your services and required functionality
        </p>
      </div>

      {/* Volume Discount Banner */}
      <div className="bg-green-500/10 border border-green-500 rounded-lg p-6">
        <div className="flex items-start gap-4">
          <Info className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
          <div>
            <h3 className="text-xl font-bold text-white mb-2">Bundle Discount Offers</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="p-4 border border-green-500/20 rounded-lg">
                <div className="text-lg font-bold text-white mb-1">Single Service</div>
                <div className="text-gray-400">Regular Price (No Discount)</div>
              </div>
              <div className="p-4 border border-green-500/20 rounded-lg">
                <div className="text-lg font-bold text-white mb-1">Two Services</div>
                <div className="text-green-500 font-bold">15% Off Total Price</div>
              </div>
              <div className="p-4 border border-green-500/20 rounded-lg">
                <div className="text-lg font-bold text-white mb-1">Three Services</div>
                <div className="text-green-500 font-bold">25% Off Total Price</div>
              </div>
            </div>
            <p className="text-sm text-gray-400">
              Note: Discounts apply to complete services. Adding extra features within a service 
              does not count as an additional service for discount purposes.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Service Categories */}
        <div className="lg:col-span-2 space-y-8">
          {(Object.entries(pricingData) as [ServiceId, typeof pricingData[ServiceId]][]).map(([serviceId, service]) => (
            <div key={serviceId} className="border border-green-500/20 rounded-lg p-6 space-y-6">
              <div className="flex items-center gap-4">
                {serviceId === 'website-chatbot' && <Bot className="w-8 h-8 text-green-500" />}
                {serviceId === 'website-mobile' && <Globe className="w-8 h-8 text-green-500" />}
                {serviceId === 'phone-agent' && <Phone className="w-8 h-8 text-green-500" />}
                <h3 className="text-2xl font-bold text-white">{service.name}</h3>
              </div>

              {/* Business Model Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={() => handleModelSelect(serviceId, 'Build & Maintain')}
                  className={`p-4 rounded-lg border text-left transition-all
                    ${selectedServices.get(serviceId)?.model === 'Build & Maintain'
                      ? 'border-green-500 bg-green-500/10'
                      : 'border-green-500/20 hover:border-green-500/40'}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center
                      ${selectedServices.get(serviceId)?.model === 'Build & Maintain'
                        ? 'border-green-500 bg-green-500'
                        : 'border-gray-400'}`}
                    >
                      {selectedServices.get(serviceId)?.model === 'Build & Maintain' && (
                        <Check className="w-3 h-3 text-black" />
                      )}
                    </div>
                    <span className="font-bold text-white">Build & Maintain</span>
                  </div>
                  <div className="text-gray-400">
                    ${service.buildAndMaintain.setup} + ${service.buildAndMaintain.monthly}/month
                  </div>
                </button>

                <button
                  onClick={() => handleModelSelect(serviceId, 'Build & Bounce')}
                  className={`p-4 rounded-lg border text-left transition-all
                    ${selectedServices.get(serviceId)?.model === 'Build & Bounce'
                      ? 'border-green-500 bg-green-500/10'
                      : 'border-green-500/20 hover:border-green-500/40'}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center
                      ${selectedServices.get(serviceId)?.model === 'Build & Bounce'
                        ? 'border-green-500 bg-green-500'
                        : 'border-gray-400'}`}
                    >
                      {selectedServices.get(serviceId)?.model === 'Build & Bounce' && (
                        <Check className="w-3 h-3 text-black" />
                      )}
                    </div>
                    <span className="font-bold text-white">Build & Bounce</span>
                  </div>
                  <div className="text-gray-400">${service.buildAndBounce.setup} one-time</div>
                </button>
              </div>

              {/* Feature Selection */}
              {selectedServices.has(serviceId) && (
                <div className="space-y-4 pt-4 border-t border-green-500/20">
                  <h4 className="text-lg font-bold text-white">Required Functionality</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(service.features).map(([featureId, feature]) => (
                      <button
                        key={featureId}
                        onClick={() => toggleFeature(serviceId, featureId as ServiceFeatureId)}
                        className={`p-4 rounded-lg border text-left transition-all
                          ${selectedServices.get(serviceId)?.features.has(featureId as ServiceFeatureId)
                            ? 'border-green-500 bg-green-500/10'
                            : 'border-green-500/20 hover:border-green-500/40'}`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center
                            ${selectedServices.get(serviceId)?.features.has(featureId as ServiceFeatureId)
                              ? 'border-green-500 bg-green-500'
                              : 'border-gray-400'}`}
                          >
                            {selectedServices.get(serviceId)?.features.has(featureId as ServiceFeatureId) && (
                              <Check className="w-3 h-3 text-black" />
                            )}
                          </div>
                          <span className="font-bold text-white">{feature.name}</span>
                        </div>
                        
                        <div className="text-gray-400 mb-2">
                          {feature.price > 0 && `+$${feature.price}`}
                          {feature.price === 0 && 'Included'}
                          {feature.monthlyPrice > 0 && selectedServices.get(serviceId)?.model === 'Build & Maintain' &&
                            ` (+$${feature.monthlyPrice}/month)`}
                        </div>

                        <ul className="space-y-1">
                          {feature.features.map((f, i) => (
                            <li key={i} className="text-sm text-gray-400 flex items-center gap-2">
                              <Check className="w-3 h-3 text-green-500" />
                              {f}
                            </li>
                          ))}
                        </ul>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Order Summary */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <div className="bg-black/50 border border-green-500/20 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-6">
              <Package className="w-6 h-6 text-green-500" />
              <h4 className="text-xl font-bold text-white">Order Summary</h4>
            </div>

            {selectedServices.size === 0 ? (
              <p className="text-gray-400 text-center py-4">
                Select services to see pricing
              </p>
            ) : (
              <div className="space-y-6">
                {Array.from(selectedServices.entries()).map(([serviceId, selected]) => {
                  const service = pricingData[serviceId];
                  const costs = calculateTotalCost(selected);
                  
                  return (
                    <div key={serviceId} className="space-y-2">
                      <div className="font-bold text-white">{service.name}</div>
                      <div className="text-gray-400">{selected.model}</div>
                      <div className="text-gray-400">Setup: ${costs.setup}</div>
                      {costs.monthly > 0 && (
                        <div className="text-gray-400">Monthly: ${costs.monthly}</div>
                      )}
                      <div className="text-sm text-gray-500">
                        Selected features:
                        <ul className="ml-4">
                          {Array.from(selected.features).map(featureId => (
                            <li key={featureId}>• {service.features[featureId].name}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  );
                })}

                <div className="border-t border-green-500/20 pt-4 space-y-2">
                  <div className="flex justify-between text-gray-400">
                    <span>Subtotal (Setup)</span>
                    <span>${totalSetupCost}</span>
                  </div>
                  {discountPercentage > 0 && (
                    <div className="flex justify-between text-green-500">
                      <span>Bundle Discount ({discountPercentage}%)</span>
                      <span>-${discountAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-white font-bold pt-2 border-t border-green-500/20">
                    <span>Total Setup Cost</span>
                    <span>${finalCost.toFixed(2)}</span>
                  </div>
                  {totalMonthly > 0 && (
                    <div className="flex justify-between text-gray-400">
                      <span>Monthly Cost</span>
                      <span>${totalMonthly}/month</span>
                    </div>
                  )}
                </div>

                {/* Email Input */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-green-500" />
                    <label htmlFor="email" className="text-white font-medium">Your Email</label>
                  </div>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailError('');
                    }}
                    placeholder="Enter your email"
                    className="w-full px-4 py-2 bg-black/30 border border-green-500/20 rounded-md 
                      text-white placeholder-gray-500 focus:outline-none focus:border-green-500"
                  />
                  {emailError && (
                    <p className="text-red-500 text-sm">{emailError}</p>
                  )}
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={!hasValidSelections || isSubmitting}
                  className="w-full px-6 py-3 bg-green-500 text-black font-bold rounded-md 
                    hover:bg-green-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Processing...
                    </span>
                  ) : (
                    'Continue & Schedule'
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}